import { ReactNode } from 'react';
import { View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Icon } from '@/common/components/Icons/Icon';
import { Label } from '@/common/components/Label/Label';
import { Stack } from '@/common/components/Stack/Stack';
import { useBreakpoints } from '@/common/hooks/useBreakpoints';
import { styles } from './NavigationSteps.styles';

export const NavigationSteps = ({
  steps,
  currentStep,
  className,
}: INavigationStepsProps) => {
  const { lg } = useBreakpoints(['lg']);
  const currentStepDescription = steps[currentStep].description;

  return (
    <Stack gap="md" className={className}>
      {lg ? (
        <Stack horizontal gap="lg">
          {steps.map((step, idx) => {
            const isCurrent = idx === currentStep;
            const isCompleted = idx < currentStep;

            return (
              <Stack key={idx} className={styles.stepContainer}>
                <Stack horizontal className={styles.labelContainer}>
                  <Stack horizontal align="center" gap="sm" className="shrink">
                    {isCompleted && (
                      <Icon icon="check" color="accentSecondary10" />
                    )}
                    <Label
                      className={styles.stepLabel}
                      bold={isCurrent}
                      color={isCurrent ? 'neutral30' : 'neutral20'}
                    >
                      {step.title}
                    </Label>
                  </Stack>
                </Stack>
                <StepLine filled={isCompleted || isCurrent} />
              </Stack>
            );
          })}
        </Stack>
      ) : (
        <Stack gap="sm">
          <Stack horizontal justify="between" gap="sm" className="w-full">
            <Label className={styles.stepLabel} bold color="neutral20">
              {steps[currentStep].title}
            </Label>
            <Label>{`${currentStep + 1}/${steps.length}`}</Label>
          </Stack>
          <Stack horizontal gap="sm" grow className="w-full">
            {steps.map((_step, idx) => {
              const isFilled = idx <= currentStep;
              return (
                <View key={idx} className={styles.stepContainer}>
                  <StepLine filled={isFilled} />
                </View>
              );
            })}
          </Stack>
        </Stack>
      )}
      {currentStepDescription && (
        <BodyText color="neutral20">{currentStepDescription}</BodyText>
      )}
    </Stack>
  );
};

function StepLine({ filled }: { filled?: boolean }) {
  const style = useAnimatedStyle(() => {
    return {
      width: withTiming(filled ? '100%' : '0%'),
    };
  });
  return (
    <View className={styles.stepLine}>
      <Animated.View className={styles.stepLineFilled} style={style} />
    </View>
  );
}

export interface INavigationStepsProps {
  steps: {
    title: string;
    description?: ReactNode;
  }[];
  currentStep: number;
  className?: string;
  noSpacing?: boolean;
}
